/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap as unboundedCssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import {
	CommentCountElement,
	ModifiedOnElement,
	OwnedByElement,
	OwnedByGroupElement,
	ReactCountElement,
	SmartLinkSize,
	SmartLinkTheme,
	SnippetBlock,
	UnresolvedAction,
} from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';
import { CardContainer, useTrackedSmartCardState } from '@atlassian/company-hub-common';
import { getImageTypeToShow, ImageRenderer } from '@atlassian/company-hub-common/media-components';

import { useResolveImage } from '../../../../../../controllers/use-resolve-image';
import { useSmartCardElementsRenderState } from '../../../../../../controllers/use-smart-card-elements-render-state';
import { LinkIcon } from '../../../link-icon';
import { CardDescription, CardTitle, FontVariant } from '../../../text-elements';
import type { CommonCardProps } from '../../../types';

import { messages } from './messages';

const unboundedStyles = unboundedCssMap({
	avatar: {
		// prevent avatar dimension from fluctuating and affecting vertical space distribution
		maxHeight: '24px',
		marginTop: '-4px',

		// Avatar is left-indented by left padding on ul , which is introduced by Prosemirror.
		// It was fixed for other type of smart card in packages/editor/editor-common/src/styles/shared/smart-card.ts
		// However, the FlexibleCard remains unfixed because it doesn't have a specific class name.
		// Hence, we need to fix it here until a more permanent solution is available.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"span[data-smart-element='OwnedByGroup'] > ul": {
			padding: 0,
			margin: 0,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& > li': {
				margin: 0,
			},
		},
	},
	snippetOverride: {
		color: 'inherit',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& div[data-smart-block]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& *': {
				color: 'inherit',
				font: token('font.body'),
			},
		},
	},
});

const styles = cssMap({
	content: {
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},
	image: {
		overflow: 'hidden',
		height: '100%',
		flex: '1 1 auto', // take rest of the available space after body is filled
	},
	floatingIcon: {
		paddingInlineStart: token('space.200'),
		top: token('space.negative.150'),
		height: '0px',
		overflow: 'visible',
		position: 'relative',
	},
	body: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	socials: {
		marginTop: 'auto',
	},
	unresolvedAction: {
		width: '100%',
		overflow: 'hidden',
		font: token('font.body'),
		textOverflow: 'ellipsis',
		color: token('color.text.subtle'),
	},
});

const MIN_TITLE_LINES = 2;
const MAX_TITLE_LINES = 5;
const MIN_DESCRIPTION_LINES = 3;
const MAX_DESCRIPTION_LINES = 7;

const getTitleDescriptionMaxLine = ({
	hasImage,
	shouldRenderMetadata,
	shouldRenderSocials,
}: {
	hasImage: boolean;
	shouldRenderMetadata?: boolean;
	shouldRenderSocials?: boolean;
}) => {
	let titleMaxLine = MIN_TITLE_LINES;
	let descriptionMaxLine = MIN_DESCRIPTION_LINES;

	if (!hasImage) {
		descriptionMaxLine += 2;
		titleMaxLine += 2;

		if (!shouldRenderMetadata) {
			descriptionMaxLine += 1;
			titleMaxLine += 1;
		}

		if (!shouldRenderSocials) {
			descriptionMaxLine += 1;
		}
	}

	if (titleMaxLine > MAX_TITLE_LINES) {
		titleMaxLine = MAX_TITLE_LINES;
	}

	if (descriptionMaxLine > MAX_DESCRIPTION_LINES) {
		descriptionMaxLine = MAX_DESCRIPTION_LINES;
	}

	return {
		titleMaxLine,
		descriptionMaxLine,
	};
};

export const MediumCardV1 = ({
	cardDimensions,
	cardId,
	link,
	isInViewMode,
	isManualVariant,
	extensionLocalId,
	imageSrc,
	imageAltText,
	contentId,
	title,
	description,
	showDescription,
	enableSnippetRenderer,
	isAvatarShown,
	isPublishDateShown,
	isSocialStatsShown,
	isIconShown,
	onCardSucceeded,
	onCardFailed,
	backgroundColor,
	textColor,
	cornerStyle,
}: CommonCardProps): JSX.Element => {
	const hasLink = !!link;
	const { formatMessage } = useIntl();

	const cardHeight = cardDimensions.height;

	const {
		setIsAvatarAvailable,
		setIsOwnerNameAvailable,
		setIsModifiedOnAvailable,
		setIsReactCountAvailable,
		setIsCommentCountAvailable,
		shouldRenderMetadata,
		shouldRenderSocials,
		shouldRenderOwnerInfo,
	} = useSmartCardElementsRenderState({
		isOwnerInfoShown: isAvatarShown && hasLink,
		isModifiedOnShown: isPublishDateShown && hasLink,
		isSocialStatsShown: isSocialStatsShown && hasLink,
	});

	const displayImageSrc = useResolveImage(link, imageSrc);
	const { hasImage } = getImageTypeToShow({ imageSrc: displayImageSrc });

	const {
		isResolved,
		errorCardStatus: hasError,
		trackAndCategorizeError,
		trackSuccess,
	} = useTrackedSmartCardState(cardId, link, onCardSucceeded, onCardFailed);

	const { titleMaxLine, descriptionMaxLine } = getTitleDescriptionMaxLine({
		hasImage,
		shouldRenderMetadata,
		shouldRenderSocials,
	});

	const cardBodyItems = hasLink
		? [
				// Icon for non-image cards
				isIconShown && !hasImage && <LinkIcon key="link-icon" link={link} />,
				// Title
				title && (
					<CardTitle key="title" maxLine={titleMaxLine} fontVariant={FontVariant.HeadingSmall}>
						{title}
					</CardTitle>
				),
				// Unresolved action if there is error and no title and no description
				hasError && !title && !description && (
					<Box key="unresolved-action" xcss={cx(styles.unresolvedAction)}>
						<UnresolvedAction />
					</Box>
				),
				// Avatar and Publish Date
				isResolved && !hasError && shouldRenderMetadata && (
					<Inline
						testId="medium-card-v1-metadata"
						key="metadata"
						space="space.050"
						separator={<Box>•</Box>}
						alignBlock="center"
					>
						{shouldRenderOwnerInfo && (
							<Inline space="space.050" alignBlock="center">
								<div css={unboundedStyles.avatar}>
									<OwnedByGroupElement onRender={setIsAvatarAvailable} />
								</div>
								<OwnedByElement color="inherit" hideFormat onRender={setIsOwnerNameAvailable} />
							</Inline>
						)}
						{!!isPublishDateShown && (
							<ModifiedOnElement
								color="inherit"
								hideDatePrefix
								onRender={setIsModifiedOnAvailable}
							/>
						)}
					</Inline>
				),
				// Description
				// AI snippet
				showDescription && enableSnippetRenderer && (
					<div key="snippet" css={unboundedStyles.snippetOverride}>
						<SnippetBlock showFooter={false} maxLines={descriptionMaxLine} text={description} />
					</div>
				),
				// normal description
				showDescription && !enableSnippetRenderer && description && (
					<CardDescription
						key="description"
						maxLine={descriptionMaxLine}
						fontVariant={FontVariant.Body}
					>
						{description}
					</CardDescription>
				),
				// Socials
				isResolved && !hasError && shouldRenderSocials && (
					<Inline
						testId="medium-card-v1-socials"
						key="socials"
						space="space.150"
						xcss={styles.socials}
					>
						<ReactCountElement color="inherit" onRender={setIsReactCountAvailable} />
						<CommentCountElement color="inherit" onRender={setIsCommentCountAvailable} />
					</Inline>
				),
			].filter(Boolean)
		: [
				// Title or placeholder
				<CardTitle key="title" maxLine={titleMaxLine} fontVariant={FontVariant.HeadingSmall}>
					{title || formatMessage(messages.emptyStatePlaceholderTitle)}
				</CardTitle>,
				// Description
				description && (
					<CardDescription
						key="description"
						maxLine={descriptionMaxLine}
						fontVariant={FontVariant.Body}
					>
						{description}
					</CardDescription>
				),
			].filter(Boolean);

	const cardContent = (
		// SSRCard respects children's height only
		<Box testId="medium-card-v1-content" xcss={cx(styles.content)} style={{ height: cardHeight }}>
			{/* Image */}
			{hasImage && (
				<Box xcss={cx(styles.image)}>
					<ImageRenderer
						contentId={contentId}
						imageSrc={displayImageSrc}
						imageAlt={imageAltText}
						shouldShowExternalSiteImage={hasLink}
					/>
				</Box>
			)}

			{/* Floating icon */}
			{isIconShown && hasLink && hasImage && (
				<Box xcss={cx(styles.floatingIcon)}>
					<LinkIcon link={link} />
				</Box>
			)}

			<Stack testId="medium-card-body" space="space.100" grow="fill" xcss={cx(styles.body)}>
				{cardBodyItems}
			</Stack>
		</Box>
	);

	return (
		<CardContainer
			id="medium-card-v1-container"
			cardHeight={cardHeight}
			isInViewMode={isInViewMode}
			cardId={cardId}
			extensionLocalId={extensionLocalId}
			isEditable={isManualVariant}
			cornerStyle={cornerStyle}
			hasLink={hasLink}
			backgroundColor={backgroundColor}
			textColor={textColor}
		>
			{hasLink ? (
				<SmartCardSSR
					id={cardId}
					appearance="inline"
					frameStyle="show"
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: isInViewMode,
						size: SmartLinkSize.Medium,
						removeBlockRestriction: true,
						hideBackground: true,
						hideElevation: true,
						hidePadding: true,
						enableSnippetRenderer,
					}}
					url={link}
					onResolve={trackSuccess}
					onError={trackAndCategorizeError}
				>
					{cardContent}
				</SmartCardSSR>
			) : (
				cardContent
			)}
		</CardContainer>
	);
};
